import type { Country } from '@seek/melways-sites';
import { maxLength } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { useEffect } from 'react';

import translations from 'src/views/shared/forms/RegistrationForm/.vocab';
import {
  useField,
  useRegistrationForm,
} from 'src/views/shared/forms/RegistrationForm/RegistrationFormContext';

import { TaxIdentificationNumberField } from './TaxIdentificationNumberField';

/**
 * SEEK markets that use a tax identification number.
 */
const TAX_COUNTRIES: Array<Extract<Country, 'ph' | 'id' | 'my'>> = [
  'ph',
  'id',
  'my',
];

type TaxIdentityCountry = (typeof TAX_COUNTRIES)[number];

/**
 * Return true if the country uses a tax identification number.
 */
export const isTaxIdentityCountry = (
  country: Country,
): country is TaxIdentityCountry =>
  TAX_COUNTRIES.includes(country as TaxIdentityCountry);

interface Props {
  country: TaxIdentityCountry;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

/**
 * Displays a tax identification number field based on the country.
 */
export const TaxIdentificationNumber = ({ country, onBlur }: Props) => {
  const { t } = useTranslations(translations);
  const { setFieldValue } = useRegistrationForm();

  const taxIdentificationNumber = useField({
    id: 'taxIdentificationNumber',
    validators: [maxLength],
    validatorProps: {
      max: 30,
    },
  });

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    taxIdentificationNumber.onBlur();
    onBlur?.(event);
  };

  /**
   * Clear the values of this field when this components unmounts or when the country changes.
   * This is to prevent the value from persisting when the user switches countries.
   */
  useEffect(
    // Returning a function is intentional so that we run this step on the cleanup lifecycle step.
    () => () => {
      setFieldValue('taxIdentificationNumber', '');
    },
    [setFieldValue, country],
  );

  switch (country.toLocaleLowerCase()) {
    case 'ph':
      return (
        <TaxIdentificationNumberField
          {...taxIdentificationNumber}
          label={t('Tax Identification Number')}
          description={t('Provide your TIN')}
          placeholder="XXXXXXXXXXXX"
          tooltip={t('TIN PH Tooltip')}
          onBlur={handleBlur}
        />
      );
    case 'id':
      return (
        <TaxIdentificationNumberField
          {...taxIdentificationNumber}
          label={t('Nomor Pendaftaran Wajib Pajak')}
          description={t('Provide your NPWP')}
          placeholder="XXXXXXXXXXXXXXXX"
          tooltip={t('NPWP Tooltip')}
          onBlur={handleBlur}
        />
      );
    case 'my':
      return (
        <TaxIdentificationNumberField
          {...taxIdentificationNumber}
          label={t('Tax Identification Number')}
          description={t('Provide your TIN')}
          placeholder="XXXXXXXXXXX"
          tooltip={t('TIN MY Tooltip')}
          onBlur={handleBlur}
        />
      );

    default:
      throw new Error(`Unsupported TIN country: ${country}`);
  }
};
